///
/// EarthBrowser
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { TextNode } from "@/earthbrowser/entities/TextNode/TextNode"
import { type Font } from "@/earthbrowser/libs/opentype.js"
import { FontCache } from "@/earthbrowser/utils/FontCache"
import { isVideoRenderMode } from "@/earthbrowser/utils/renderMode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { StormTrackPoint } from "./HurricanesTable"
import { destinationLatLon } from "./stormTrack"

const cLabelFont = "/fonts/Inter-Bold.otf"

// Marker circle around each forecast position
const cMarkerRadiusKm = 16
const cMarkerSamples = 36
const cMarkerWidth = 2

// Flat text labels sit south of the marker so they don't cover the track
const cLabelFontSize = 26
const cLabelSouthOffsetKm = 45

/**
 * Labels are lifted above the surface so they clear the terrain and the
 * marker geometry; the markers stay draped on the surface with the track.
 * From regional zoom the parallax is imperceptible.
 */
export const cStormLabelLiftKm = 20

/**
 * Forecast point markers along a storm's forecast track: a small category-
 * colored circle at each advisory forecast position, with a flat "Tue 2 PM ·
 * Cat 3" label in the viewer's local time. Labels are hidden until the storm
 * is focused so a basin with several storms doesn't turn into a text cloud.
 */
export class StormForecastPointsNode extends NodeBase {
	private readonly labelContainer = new NodeBase()

	constructor(context: StovokorContext, forecast: StormTrackPoint[], altitudeKm: number) {
		super()

		// TAU=0 duplicates the current position marker; label the future only
		const points = forecast.filter(p => (p.tau ?? 0) > 0)
		if (points.length === 0) return

		for (const point of points) {
			this.addNode(this.buildMarker(context, point, altitudeKm))
		}

		this.labelContainer.visible = false
		this.addNode(this.labelContainer)

		void this.buildLabels(context, points, altitudeKm)
	}

	/** Show the time/category labels while the storm is selected */
	public setLabelsVisible(visible: boolean): void {
		this.labelContainer.visible = visible
	}

	/** Small circle draped around the forecast position, category colored */
	private buildMarker(context: StovokorContext, point: StormTrackPoint, altitudeKm: number): PolylineNode {
		const positions: vec3[] = []
		const angularRadius = cMarkerRadiusKm / 6371
		for (let i = 0; i < cMarkerSamples; i++) {
			const azimuth = (i / cMarkerSamples) * trig.two_pi
			const { lat, lon } = destinationLatLon(point.lat, point.lon, azimuth, angularRadius)
			positions.push(new geographic(lon * trig.rad, lat * trig.rad, altitudeKm).cartesian)
		}
		return new PolylineNode(context, {
			positions,
			width: cMarkerWidth,
			color: DesignColors.stormCategoryColor(point.category, point.wind_kt),
			loop: true,
			depthWrite: false,
			polygonOffset: cSurfaceOverlayDepthBias,
		})
	}

	private async buildLabels(context: StovokorContext, points: StormTrackPoint[], altitudeKm: number): Promise<void> {
		const font = await FontCache.load(cLabelFont)
		for (const point of points) {
			this.labelContainer.addNode(buildFlatLabel(
				context,
				font,
				forecastPointLabel(point),
				DesignColors.STORM_LABEL_TEXT,
				point.lat,
				point.lon,
				altitudeKm + cStormLabelLiftKm,
				cLabelSouthOffsetKm
			))
		}
	}
}

/** "Tue 2 PM · Cat 3" in the viewer's local time zone */
export function forecastPointLabel(point: StormTrackPoint): string {
	const date = new Date(point.t)
	// Recordings are watched anywhere: label in UTC ("Fri 12Z") instead of
	// the rendering machine's zone
	if (isVideoRenderMode()) {
		const weekday = date.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })
		return `${weekday} ${String(date.getUTCHours()).padStart(2, "0")}Z · ${pointCategoryLabel(point)}`
	}
	const weekday = date.toLocaleDateString(undefined, { weekday: "short" })
	const hour = date.toLocaleTimeString(undefined, { hour: "numeric" })
	return `${weekday} ${hour} · ${pointCategoryLabel(point)}`
}

/** Short intensity label for a single track point */
export function pointCategoryLabel(point: StormTrackPoint): string {
	if (point.category >= 1) return `Cat ${point.category}`
	return (point.wind_kt ?? 0) >= 34 ? "TS" : "TD"
}

/**
 * Flat 3D text lying in the local tangent plane at a lat/lon, using the same
 * orientation recipe as the synoptic pressure-center labels. The southOffset
 * pushes the (origin-centered) text south of the anchor so it doesn't cover
 * the marker it annotates. A dark drop shadow slightly southeast and below
 * keeps the white text readable over the bright cloud shield.
 */
export function buildFlatLabel(
	context: StovokorContext,
	font: Font,
	text: string,
	color: typeof DesignColors.STORM_LABEL_TEXT,
	latDeg: number,
	lonDeg: number,
	altitudeKm: number,
	southOffsetKm: number
): NodeBase {
	const geo = new geographic(lonDeg * trig.rad, latDeg * trig.rad, altitudeKm)
	const xRot = matrix.axisRotation(vec3.unitX, geo.latitude + trig.half_pi)
	const yRot = matrix.axisRotation(vec3.unitY, geo.longitude + trig.pi)

	const anchor = new NodeBase()
	anchor.transform = matrix.translate(geo.cartesian).premultiply(yRot.premultiply(xRot))

	// In this tangent frame x is east, -y is up (away from the surface) and
	// z is south; the shadow sits lower so the label always draws over it
	const shadowNode = new TextNode(context, text, font, cLabelFontSize)
	shadowNode.color = DesignColors.STORM_LABEL_SHADOW
	shadowNode.transform = matrix.axisRotation(vec3.unitX, -trig.half_pi)
		.rotateBy(vec3.unitY, trig.pi)
		.translateBy(1.5, -1.2, southOffsetKm + 1.5)
	anchor.addNode(shadowNode)

	const textNode = new TextNode(context, text, font, cLabelFontSize)
	textNode.color = color
	textNode.transform = matrix.axisRotation(vec3.unitX, -trig.half_pi)
		.rotateBy(vec3.unitY, trig.pi)
		.translateBy(0, -2, southOffsetKm)
	anchor.addNode(textNode)
	return anchor
}
